import { ChangeDetectorRef, Component } from '@angular/core';
import { BroadcastEvents } from 'src/app/enums/broadcast-events';
import { ElementAttribute } from 'src/app/enums/element-attribute';
import { SelectedBurialsTab } from 'src/app/enums/selected-burials-tab';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { BurialsService } from 'src/app/services/burials.service';
import { CredentialsService } from 'src/app/services/credentials.service';
import { ModalService } from 'src/app/services/modal.service';

declare function initSelect(): void;
declare function updateMaterialTextFields(): void;
declare function initQRCodeInFieldWithID(fieldID: string, initialText: string): void;
declare function setReadonlyOnInputsToState(readonlyState: boolean, isForced: boolean): void;
declare function initBurialsApplicationValues(): void;
declare function setSelectValue(select: string, value: string): void;

@Component({
  selector: 'app-application',
  templateUrl: './application.component.html',
  styleUrls: ['./application.component.css']
})
export class ApplicationComponent {

  currentData: any = this.modalService.getModalData();
  burialData: any = this.currentData.burialData;
  showInputReturnAddress: boolean = false;
  showInputReturnTime: boolean = false;
  returnDateLabel: string = 'Terminwunsch';
  returnTimeLabel: string = 'Zum Versand bereit bis spätestens';
  secondCover?: string;
  personalDataCache: any;

  constructor(private modalService: ModalService,
    private credentialsService: CredentialsService,
    private changeDetectorRef: ChangeDetectorRef,
    private broadcastService: BroadcastService,
    private burialsService: BurialsService) {

    this.broadcastService.on(BroadcastEvents.reloadData).subscribe(_ => this.insertDataToFields());

    this.setReturnTypeValue(this.burialData.inputReturnType ?? this.modalService.getModalData().d_urndeliverytype);
  }

  ngAfterViewInit() {
    initQRCodeInFieldWithID('qrCodeView', '');
    setReadonlyOnInputsToState(this.modalService.shouldBeReadOnly, false);
    initBurialsApplicationValues();

    // Beim Öffnen eines neuen Auftrags alle Felder explizit leeren,
    // um zu verhindern, dass Firefox alte Werte wiederherstellt
    if (!this.modalService.shouldBeReadOnly && this.isNewBurial()) {
      this.clearAllInputFieldsForNewBurial();
    }

    for (var key in this.burialData) {
      if (key && key != 'qrstring') {
        this.setValueForKey(key, this.burialData[key]);
      }
    }

    initSelect();
    this.insertDataToFields();
  }

  checkSelectFieldRestore(selectField: any, fieldValueChanged: any) {
    if (fieldValueChanged || !selectField.id) { return; }
    selectField.value = 0;
    delete this.burialData[selectField.id];
    this.modalService.setModalData('burialData', this.burialData);
    initSelect();
  }

  didChangeBurialFieldWithClass(fieldClass: string, isSelect: boolean = false) {
    const field = this.credentialsService.parseElementBy(ElementAttribute.class, fieldClass);
    if (!field) { return; }

    var fieldValue: any = field.value;

    var fieldValueChanged: boolean = false;
    if (this.burialData[fieldClass] != fieldValue) {
      fieldValueChanged = true;
    }

    if (fieldClass == 'secondCover') {
      this.burialData[fieldClass] = this.secondCover;
      this.modalService.didInputForClass(fieldClass);
      this.modalService.setModalData('burialData', this.burialData);
    } else {
      this.burialData[fieldClass] = fieldValue;
      this.modalService.didInputForClass(fieldClass);
      this.modalService.setModalData('burialData', this.burialData);
    }

    if (fieldClass == 'inputLastName') {
      this.modalService.inputLastNameDidChange(fieldValue);
    } else if (fieldClass == 'inputName') {
      this.modalService.inputNameDidChange(fieldValue);
    } else if (fieldClass == 'inputReturnType') {
      this.checkSelectFieldRestore(field, fieldValueChanged);

      if (fieldValue == '1') {
        this.showInputReturnAddress = true;
        this.showInputReturnTime = true;
        this.returnDateLabel = 'Terminwunsch';
        this.returnTimeLabel = 'Zum Versand bereit bis spätestens';
      } else if (fieldValue == '2') {
        this.showInputReturnAddress = false;
        this.showInputReturnTime = true;
        this.returnDateLabel = 'Terminwunsch';
        this.returnTimeLabel = 'Zur Abholung bereit bis spätestens';
      } else if (fieldValue == '3') {
        this.showInputReturnAddress = false;
        this.showInputReturnTime = true;
        this.returnDateLabel = 'Terminwunsch';
        this.returnTimeLabel = 'Rückführung bis spätestens';
      } else {
        this.showInputReturnAddress = false;
        this.showInputReturnTime = false;
      }

      setTimeout(() => {
        initBurialsApplicationValues();
      }, 0);
    } else if (fieldClass == 'inputGender') {
      this.checkSelectFieldRestore(field, fieldValueChanged);
    } else if (fieldClass == 'inputFuneralType') {
      this.checkSelectFieldRestore(field, fieldValueChanged);
    } else if (fieldClass == 'inputDeathType') {
      this.checkSelectFieldRestore(field, fieldValueChanged);
    } else if (fieldClass == 'inputUrnType') {
      this.checkSelectFieldRestore(field, fieldValueChanged);
    }else if (fieldClass == 'inputClientSalutation') {
      this.checkSelectFieldRestore(field, fieldValueChanged);
    }
  }

  setValueForKey(fieldClass: string, value: string) {
    if (fieldClass == 'secondCover') {
      this.secondCover = value;
    } else {
      const field = this.credentialsService.parseElementBy(ElementAttribute.class, fieldClass);
      if (!field) { return; }
      field.value = value;
    }
  }

  private isNewBurial(): boolean {
    // Prüfe, ob es ein neuer Auftrag ist (keine Daten außer möglicherweise qrstring)
    const dataKeys = Object.keys(this.burialData || {});
    return dataKeys.length === 0 || (dataKeys.length === 1 && dataKeys[0] === 'qrstring');
  }

  private clearAllInputFieldsForNewBurial() {
    // Liste aller Input-Felder, die geleert werden müssen
    const inputFields = [
      'inputLastName',
      'inputName',
      'inputBirthName',
      'inputStreet',
      'inputZip',
      'inputCity',
      'inputBirthDate',
      'inputBirthPlace',
      'inputDeathDate',
      'inputDeathTime',
      'inputDeathPlace',
      'inputBurialPlace',
      'registryOffice',
      'registryOfficeNumber',
      'inputReturnDate',
      'inputReturnTime',
      'inputReturnLastName',
      'inputReturnName',
      'inputReturnStreet',
      'inputReturnZip',
      'inputReturnCity',
      'inputClientLastName',
      'inputClientName',
      'inputClientStreet',
      'inputClientZip',
      'inputClientCity',
      'inputUrnStyle',
      'inputBurialInfo',
      'inputDespatchID'
    ];

    // Alle Input-Felder leeren
    inputFields.forEach(fieldClass => {
      this.setValueForKey(fieldClass, '');
    });

    // Select-Felder auf Standardwert zurücksetzen
    const selectFields = [
      { id: 'inputGender', defaultValue: '0' },
      { id: 'inputReturnType', defaultValue: '0' },
      { id: 'inputClientSalutation', defaultValue: '0' },
      { id: 'inputUrnType', defaultValue: '0' }
    ];

    selectFields.forEach(selectField => {
      setSelectValue(selectField.id, selectField.defaultValue);
    });

    // Materialize Textfelder aktualisieren
    updateMaterialTextFields();
  }

  private setReturnTypeValue(returntypeValue: string) {
    if (returntypeValue == '1') {
      this.showInputReturnAddress = true;
      this.showInputReturnTime = true;
      this.returnDateLabel = 'Terminwunsch';
      this.returnTimeLabel = 'Zum Versand bereit bis spätestens';
    } else if (returntypeValue == '2') {
      this.showInputReturnTime = true;
      this.returnDateLabel = 'Terminwunsch';
      this.returnTimeLabel = 'Zur Abholung bereit bis spätestens';
    } else if (returntypeValue == '3') {
      this.showInputReturnTime = true;
      this.returnDateLabel = 'Terminwunsch';
      this.returnTimeLabel = 'Rückführung bis spätestens';
    }
  }

  selectedTab(): SelectedBurialsTab | undefined {
    return this.modalService.selectedTab;
  }

  clientAutoButtonClickedWithType(clickedType: string) {
    this.setValueForKey('inputClientLastName', '');
    this.didChangeBurialFieldWithClass('inputClientLastName');
    this.setValueForKey('inputClientName', '');
    this.didChangeBurialFieldWithClass('inputClientName');
    this.setValueForKey('inputClientStreet', '');
    this.didChangeBurialFieldWithClass('inputClientStreet');
    this.setValueForKey('inputClientZip', '');
    this.didChangeBurialFieldWithClass('inputClientZip');
    this.setValueForKey('inputClientCity', '');
    this.didChangeBurialFieldWithClass('inputClientCity');

    if (clickedType == 'B') {
      const bestatter = localStorage.getItem('bestatter');
      if (localStorage.hasOwnProperty('bestatter') && bestatter) {
        var currentUndertaker = JSON.parse(bestatter);
        if (currentUndertaker && currentUndertaker.hasOwnProperty('adresse1') && currentUndertaker.adresse1) {
          this.setValueForKey('inputClientLastName', currentUndertaker.adresse1);
          this.didChangeBurialFieldWithClass('inputClientLastName');
        }
        if (currentUndertaker && currentUndertaker.hasOwnProperty('adresse2') && currentUndertaker.adresse2) {
          this.setValueForKey('inputClientName', currentUndertaker.adresse2);
          this.didChangeBurialFieldWithClass('inputClientName');
        }
        if (currentUndertaker && currentUndertaker.hasOwnProperty('strasse') && currentUndertaker.strasse) {
          this.setValueForKey('inputClientStreet', currentUndertaker.strasse);
          this.didChangeBurialFieldWithClass('inputClientStreet');
        }
        if (currentUndertaker && currentUndertaker.hasOwnProperty('plz') && currentUndertaker.plz) {
          this.setValueForKey('inputClientZip', currentUndertaker.plz);
          this.didChangeBurialFieldWithClass('inputClientZip');
        }
        if (currentUndertaker && currentUndertaker.hasOwnProperty('ort') && currentUndertaker.ort) {
          this.setValueForKey('inputClientCity', currentUndertaker.ort);
          this.didChangeBurialFieldWithClass('inputClientCity');
        }
      }
    } else if (clickedType == 'V') {
      if (this.burialData && this.burialData.hasOwnProperty('inputLastName') && this.burialData.inputLastName) {
        this.setValueForKey('inputClientLastName', this.burialData.inputLastName);
        this.didChangeBurialFieldWithClass('inputClientLastName');
      }
      if (this.burialData && this.burialData.hasOwnProperty('inputStreet') && this.burialData.inputStreet) {
        this.setValueForKey('inputClientStreet', this.burialData.inputStreet);
        this.didChangeBurialFieldWithClass('inputClientStreet');
      }
      if (this.burialData && this.burialData.hasOwnProperty('inputZip') && this.burialData.inputZip) {
        this.setValueForKey('inputClientZip', this.burialData.inputZip);
        this.didChangeBurialFieldWithClass('inputClientZip');
      }
      if (this.burialData && this.burialData.hasOwnProperty('inputCity') && this.burialData.inputCity) {
        this.setValueForKey('inputClientCity', this.burialData.inputCity);
        this.didChangeBurialFieldWithClass('inputClientCity');
      }
    }

    updateMaterialTextFields();
  }

  //This function is the old personal_data controller, and should only be used when showing personal_data
  insertDataToFields() {
    if (!this.modalService.selectedTab || this.modalService.selectedTab != SelectedBurialsTab.personalData) { return; }

    const newData = this.modalService.getModalData();
    if (newData) {
      this.personalDataCache = newData;
    }
    if (!this.personalDataCache) { return; }

    //setSelectValue('inputDeathType', this.personalDataCache.v_deathtype ?? '0');
    this.setValueForKey('inputName', this.personalDataCache.v_frontname ?? '');
    this.setValueForKey('inputLastName', this.personalDataCache.v_lastname ?? '');
    this.setValueForKey('inputBirthName', this.personalDataCache.v_birthname ?? '');
    setSelectValue('inputGender', this.personalDataCache.v_gender ?? '0');
    this.setValueForKey('inputStreet', this.personalDataCache.v_street ?? '');
    this.setValueForKey('inputZip', this.personalDataCache.v_zip ?? '');
    this.setValueForKey('inputCity', this.personalDataCache.v_city ?? '');
    this.setValueForKey('inputBirthDate', this.burialsService.readableDateFromString(this.personalDataCache.v_birthday) ?? '');
    this.setValueForKey('inputBirthPlace', this.personalDataCache.v_birthplace ?? '');
    this.setValueForKey('inputDeathDate', this.burialsService.readableDateFromString(this.personalDataCache.v_deathday) ?? '');
    this.setValueForKey('inputDeathPlace', this.personalDataCache.v_deathplace ?? '');
    this.setValueForKey('inputDeathTime', this.burialsService.readableTimeFromString(this.personalDataCache.v_deathtime) ?? '');
    this.setValueForKey('registryOffice', this.personalDataCache.v_registryoffice ?? '');
    this.setValueForKey('inputReturnType', this.personalDataCache.d_urndeliverytype ?? '');
    setSelectValue('inputClientSalutation',this.burialsService.salutationTypeIDByReadableSalutation(this.personalDataCache.a_salutation) ?? '0');
    this.setValueForKey('inputReturnDate', this.burialsService.readableDateFromString(this.personalDataCache.d_urndeliverydate) ?? '');
    this.setValueForKey('inputReturnTime', this.burialsService.readableTimeFromString(this.personalDataCache.d_urndeliverytime) ?? '');
    //this.setValueForKey('inputAddress', this.personalDataCache.d_nameone + " " + this.personalDataCache.d_nametwo ?? '');
    this.setValueForKey('inputReturnName', this.personalDataCache.d_nametwo ?? '');
    this.setValueForKey('inputReturnLastName', this.personalDataCache.d_nameone ?? '');
    this.setValueForKey('inputReturnStreet', this.personalDataCache.d_street ?? '');
    this.setValueForKey('inputReturnZip', this.personalDataCache.d_zip ?? '');
    this.setValueForKey('inputReturnCity', this.personalDataCache.d_city ?? '');
    //this.setValueForKey('inputDespatchDate', this.burialsService.readableDateFromString(this.personalDataCache.d_departmentdate) ?? '');
    this.setValueForKey('inputDespatchID', this.personalDataCache.d_deliveryid ?? '');
    //setSelectValue('inputClientSalutation', this.burialsService.salutationTypeIDByReadableSalutation(this.personalDataCache.a_salutation) ?? '0');
    this.setValueForKey('inputClientName', this.personalDataCache.a_name ?? '');
    this.setValueForKey('inputClientLastName', this.personalDataCache.a_lastname ?? '');
    this.setValueForKey('inputClientStreet', this.personalDataCache.a_street ?? '');
    this.setValueForKey('inputClientZip', this.personalDataCache.a_zip ?? '');
    this.setValueForKey('inputClientCity', this.personalDataCache.a_city ?? '');
    setSelectValue('inputUrnType', this.personalDataCache.urntype ?? '0');
    this.setValueForKey('inputUrnStyle', this.personalDataCache.urnstyle ?? '');
    
    // Nur bei neuen Aufträgen das Feld anzeigen und befüllen
    if (!this.personalDataCache || !this.personalDataCache.contractinfo) {
      this.setValueForKey('inputBurialInfo', '');
    } else {
      // Bei bestehenden Aufträgen das Feld leeren
      this.setValueForKey('inputBurialInfo', '');
    }

    updateMaterialTextFields();
    setReadonlyOnInputsToState(true, true);
  }

}
